// ProofreadModal.tsx
// NOTE: When editing this file, update frontend/src/__tests__/ProofreadModal.test.tsx
// and run `../scripts/test-frontend.sh ProofreadModal`.
import React from 'react';
import type { TourProps } from 'antd';
import { Modal, Button, Select, Checkbox, Tooltip, Input, Tag } from 'antd';
import { QuestionCircleOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import AIModelSelect from './AIModelSelect';
import { useSelectedAIModel } from './hooks/useSelectedAIModel';

import InsufficientCreditsAlert from './components/InsufficientCreditsAlert';
import FrenchNarrationInfo, { FrenchNarrationTooltipContent } from './components/FrenchNarrationInfo';
import RecommendedTag from './components/RecommendedTag'
import ModalCostBreakdown, { type ModalCostBreakdownRow } from './components/ModalCostBreakdown';

import { CreditBalance as CreditBalanceType, FrenchNarrativeTone } from './types';
import type { CreditTopUpContext } from './types/creditTopUp';
import {
  getTargetLanguageFromPair,
  splitLanguagePair,
} from './utils/languagePairs';
import { 
  LANGUAGE_CONFIG,
  getQuotationStyle,
  hasQuotationOptions,
  getDefaultQuotationStyle,
  getQuotationTooltip 
} from './LanguageConfig';
import {
  hasPendingProofreadReviewForRow,
  normalizeProofreadVersionValue,
} from './utils/proofreadState';
import { resolveFrontendApiBase } from './utils/apiBase';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import { tutorialVideoLinks } from './tutorialVideoLinks';

// Temporarily disable queued/batch proofreading while validation/proofreading systems stabilize
const BATCH_PROOFREADING_ENABLED = false;
const AUTO_FRENCH_TONE_VALUE = 'AI Detect'
const API_BASE = resolveFrontendApiBase();
type ProofreadPassMode = 'missing' | 'retry_failed' | 'next_pass';

type ProofreadingPassSummary = {
  latestPass?: {
    passNumber?: number;
    status?: string;
    mode?: string;
  } | null;
  effectivePass?: {
    passNumber?: number;
    status?: string;
    legacy?: boolean;
  } | null;
  pendingReviewCount?: number;
  missingCount?: number;
  failedCount?: number;
  nextPassNumber?: number;
  allowedModes?: ProofreadPassMode[];
  targetChapterGuidsByMode?: Partial<Record<ProofreadPassMode, string[]>>;
  blockingReason?: string;
  structuralChapterCount?: number;
  nextPassRequiresConfirmation?: boolean;
  repeatPassAvailable?: boolean;
};

interface ProofreadModalProps {
  isVisible: boolean;
  onCancel: () => void;
  onProofread: () => void;
  selectedChapter: any;
  proofreadVersion: string;
  setProofreadVersion: (value: string) => void;
  isProofreadChecked: boolean;
  setIsProofreadChecked: (value: boolean) => void;
  autoAcceptAllChanges: boolean;
  setAutoAcceptAllChanges: (value: boolean) => void;
  isSavingAutoAcceptPreference?: boolean;
  explanationLanguage: string;
  setExplanationLanguage: (value: string) => void;
  isSavingExplanationLanguagePreference?: boolean;
  isEditingNotesChecked: boolean;
  setIsEditingNotesChecked: (value: boolean) => void;
  quotationStyle: string;
  setQuotationStyle: (value: string) => void;
  proofreadAdditionalInstructions: string;
  setProofreadAdditionalInstructions: (value: string) => void;  
  selectedAIModel: string;
  setSelectedAIModel: (value: string) => void;
  creditBalance: CreditBalanceType | null;
  calculateAdjustedCost: () => number;
  hasEligibleProofreadChapters: boolean;
  hasUnproofreadNotesChapters: boolean;
  pendingProofreadReviewCount?: number;
  proofreadingPassSummary?: ProofreadingPassSummary | null;
  bulkProofreadMode?: ProofreadPassMode;
  setBulkProofreadMode?: (value: ProofreadPassMode) => void;
  isLoadingProofreadingPassSummary?: boolean;
  canChapterBeProofread: boolean;
  hasChapterBeenProofread: boolean;
  hasChapterBeenEditNotes: boolean;
  hasUntranslatedChaptersForProofread: boolean;
  isLoadingTranslationAvailability?: boolean;
  book: any;
  formatLangDisplayText: (pair: string) => string;
  bookLanguage: string;
  frenchTone: string;
  setFrenchTone: (value: string) => void;
  useBatch: boolean;
  setUseBatch: (value: boolean) => void;
  isLangTesterUser: boolean;
  onTopUpCredits?: (context: CreditTopUpContext) => void;
  tourUserId?: string;
}

const ProofreadModal: React.FC<ProofreadModalProps> = ({
  isVisible,
  onCancel,
  onProofread,
  selectedChapter,
  proofreadVersion,
  setProofreadVersion,
  isProofreadChecked,
  setIsProofreadChecked,
  autoAcceptAllChanges,
  setAutoAcceptAllChanges,
  isSavingAutoAcceptPreference = false,
  explanationLanguage,
  setExplanationLanguage,
  isSavingExplanationLanguagePreference = false,
  isEditingNotesChecked,
  setIsEditingNotesChecked,
  quotationStyle,
  setQuotationStyle,
  proofreadAdditionalInstructions,
  setProofreadAdditionalInstructions,  
  selectedAIModel,
  setSelectedAIModel,
  creditBalance,
  calculateAdjustedCost,
  hasEligibleProofreadChapters,
  hasUnproofreadNotesChapters,
  pendingProofreadReviewCount = 0,
  proofreadingPassSummary = null,
  bulkProofreadMode = 'missing',
  setBulkProofreadMode,
  isLoadingProofreadingPassSummary = false,
  canChapterBeProofread,
  hasChapterBeenProofread,
  hasChapterBeenEditNotes,
  hasUntranslatedChaptersForProofread,
  isLoadingTranslationAvailability = false,
  book,
  formatLangDisplayText,
  bookLanguage,
  frenchTone,
  setFrenchTone,
  useBatch,
  setUseBatch,
  isLangTesterUser,
  onTopUpCredits,
  tourUserId,
}) => {
  const normalizedProofreadVersion = React.useMemo(
    () => normalizeProofreadVersionValue(proofreadVersion),
    [proofreadVersion],
  )
  const originalLanguageLabel = (bookLanguage || 'Original').trim() || 'Original';
  
  const debugProofread = React.useMemo(() => {
    try {
      return localStorage.getItem('debugProofreadModal') === 'true'
    } catch (_e) {
      return false
    }
  }, [])
  const debugStamp = React.useCallback((label: string) => {
    if (!debugProofread) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    console.log(`[ProofreadModal] ${label} @ ${Math.round(now)}ms`)
  }, [debugProofread])

  const selectedModelData = useSelectedAIModel(selectedAIModel);
  const [showReadinessDetails, setShowReadinessDetails] = React.useState(false);
  const [isCostBreakdownModalVisible, setIsCostBreakdownModalVisible] = React.useState(false);
  const batchFeatureEnabled = BATCH_PROOFREADING_ENABLED;
  const effectiveUseBatch = batchFeatureEnabled ? useBatch : false;

  React.useEffect(() => {
    if (!isVisible) return
    debugStamp('ProofreadModal:visible')
  }, [isVisible, debugStamp])
  
  const baseCost = React.useMemo(() => calculateAdjustedCost(), [calculateAdjustedCost]);
  const getModelDiscountPercent = React.useCallback(() => {
    const modelPct = Number(selectedModelData?.discount || 0);
    return Math.max(0, modelPct);
  }, [selectedModelData?.discount]);

  const getModelPremiumPercent = React.useCallback(() => {
    const premiumPct = Number(selectedModelData?.premium || 0);
    return Math.max(0, premiumPct);
  }, [selectedModelData?.premium]);

  const getTotalDiscountPercent = React.useCallback(() => {
    const modelPct = getModelDiscountPercent();
    const batchPct = effectiveUseBatch ? 5 : 0;
    return Math.max(0, modelPct + batchPct);
  }, [getModelDiscountPercent, effectiveUseBatch]);

  const getNetModelAdjustmentPercent = React.useCallback(() => {
    return getModelPremiumPercent() - getTotalDiscountPercent();
  }, [getModelPremiumPercent, getTotalDiscountPercent]);

  const adjustedCost = React.useMemo(() => {
    const netPct = getNetModelAdjustmentPercent();
    if (!netPct) {
      return baseCost;
    }
    return Math.max(0, Math.ceil(baseCost * (1 + netPct / 100)));
  }, [baseCost, getNetModelAdjustmentPercent]);

  const availableCredits = React.useMemo(() => {
    const raw = creditBalance?.available_balance ?? creditBalance?.credit_balance
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw
    }
    if (typeof raw === 'string') {
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
    return null
  }, [creditBalance]);

  const proofreadShortfallCredits = React.useMemo(() => {
    if (typeof availableCredits !== 'number') return null
    return Math.max(Math.ceil(adjustedCost - availableCredits), 0)
  }, [adjustedCost, availableCredits]);
  const hasEnoughCreditsForProofread = React.useMemo(() => {
    if (typeof availableCredits !== 'number') return false
    return availableCredits >= adjustedCost
  }, [adjustedCost, availableCredits]);
  const formatCredits = React.useCallback((value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)));
  }, []);
  const handleTopUpCredits = React.useCallback(() => {
    if (!onTopUpCredits || typeof availableCredits !== 'number') return
    const requiredCredits = Math.max(0, Math.ceil(adjustedCost))
    const normalizedAvailable = Math.max(0, Math.floor(availableCredits))
    onTopUpCredits({
      source: 'proofread_modal',
      requiredCredits,
      availableCredits: normalizedAvailable,
      shortfallCredits: Math.max(requiredCredits - normalizedAvailable, 0),
    })
  }, [adjustedCost, availableCredits, onTopUpCredits]);

  // Determine if batch is eligible (Gemini 2.5 Pro or ChatGPT-5)
  const isBatchEligible = React.useMemo(() => {
    const provider = (selectedModelData?.provider || '').toLowerCase()
    const apiModel = (selectedModelData?.apiModel || '').toLowerCase()
    const isGemini = apiModel.includes('gemini2.5pro') || apiModel.includes('gemini-2.5-pro')
    const isGPT5 = provider === 'openai' && (apiModel.includes('gpt-5') || apiModel.includes('chatgpt-5') || apiModel.includes('chatgpt5'))
    return isGemini || isGPT5
  }, [selectedModelData])

  React.useEffect(() => {
    if (!batchFeatureEnabled && useBatch) {
      setUseBatch(false);
      return;
    }
    if (batchFeatureEnabled && !isBatchEligible && useBatch) {
      setUseBatch(false);
    }
  }, [batchFeatureEnabled, isBatchEligible, useBatch, setUseBatch]);

  const needsModel = !(selectedAIModel || '').trim();

  // Calculate current language for quotation style
  const getCurrentLanguage = () => {
    if (normalizedProofreadVersion === 'Original') {
      return bookLanguage;
    }
    return getTargetLanguageFromPair(normalizedProofreadVersion) || '';
  };

  const currentLanguage = getCurrentLanguage();
  const hasSelectedVersion = Boolean((normalizedProofreadVersion || '').trim());
  const sourceForCurrentLanguage =
    currentLanguage && currentLanguage !== bookLanguage ? bookLanguage : undefined;
  const isCheckingBookProofreadEligibility = Boolean(!selectedChapter && isLoadingProofreadingPassSummary);
  const bookProofreadActionUnavailable = Boolean(
    !selectedChapter && !isCheckingBookProofreadEligibility && !hasEligibleProofreadChapters
  );
  const bookEditingNotesActionUnavailable = Boolean(!selectedChapter && !hasUnproofreadNotesChapters);
  const proofreadSelectionDisabled = Boolean(selectedChapter ? !canChapterBeProofread : bookProofreadActionUnavailable);
  const editingNotesSelectionDisabled = Boolean(
    normalizedProofreadVersion !== "Original" ||
    (selectedChapter ? !canChapterBeProofread : bookEditingNotesActionUnavailable)
  );
  const effectiveIsProofreadChecked = Boolean(isProofreadChecked && !proofreadSelectionDisabled);
  const repeatPassAvailable = !selectedChapter && !isLoadingProofreadingPassSummary
    && (proofreadingPassSummary?.repeatPassAvailable ?? proofreadingPassSummary?.allowedModes?.includes('next_pass'))
    && (proofreadingPassSummary?.pendingReviewCount || 0) === 0;
  const effectiveIsEditingNotesChecked = Boolean(isEditingNotesChecked && !editingNotesSelectionDisabled);
  const hasEffectiveProofreadAction = Boolean(effectiveIsProofreadChecked || effectiveIsEditingNotesChecked);
  const needsQuotationStyle = effectiveIsProofreadChecked && hasQuotationOptions(currentLanguage) && !quotationStyle;
  const isFrenchTarget = currentLanguage === 'French';
  const needsFrenchTone = effectiveIsProofreadChecked && isFrenchTarget && !frenchTone;
  const allowedBulkProofreadModes = React.useMemo(
    () => proofreadingPassSummary?.allowedModes || [],
    [proofreadingPassSummary?.allowedModes],
  );
  const selectedBulkProofreadModeBlocked = Boolean(
    !selectedChapter && effectiveIsProofreadChecked && proofreadingPassSummary
    && !allowedBulkProofreadModes.includes(bulkProofreadMode)
  );
  const bulkProofreadEligibilityPending = Boolean(
    !selectedChapter && effectiveIsProofreadChecked && isLoadingProofreadingPassSummary
  );
  const getBulkModeTargetCount = React.useCallback((mode: ProofreadPassMode) => {
    return proofreadingPassSummary?.targetChapterGuidsByMode?.[mode]?.length || 0;
  }, [proofreadingPassSummary?.targetChapterGuidsByMode]);
  const bulkProofreadModeOptions = React.useMemo(() => {
    return allowedBulkProofreadModes.map((mode) => {
      if (mode === 'retry_failed') {
        const passNumber = proofreadingPassSummary?.latestPass?.passNumber || proofreadingPassSummary?.effectivePass?.passNumber || 1;
        return {
          value: mode,
          label: `Retry failed chapters from pass ${passNumber}`,
          count: getBulkModeTargetCount(mode),
        };
      }
      if (mode === 'next_pass') {
        const passNumber = proofreadingPassSummary?.nextPassNumber || 2;
        return {
          value: mode,
          label: `Re-proofread all content (pass ${passNumber})`,
          count: getBulkModeTargetCount(mode),
        };
      }
      return {
        value: mode,
        label: 'Proofread missing chapters',
        count: getBulkModeTargetCount(mode),
      };
    });
  }, [
    allowedBulkProofreadModes,
    getBulkModeTargetCount,
    proofreadingPassSummary?.effectivePass?.passNumber,
    proofreadingPassSummary?.latestPass?.passNumber,
    proofreadingPassSummary?.nextPassNumber,
  ]);
  const selectedBulkProofreadModeOption = React.useMemo(
    () => bulkProofreadModeOptions.find(option => option.value === bulkProofreadMode),
    [bulkProofreadMode, bulkProofreadModeOptions],
  );
  const getNoticeTone = (type: 'error' | 'warning' | 'success') => {
    if (type === 'warning') return 'warning';
    if (type === 'success') return 'success';
    return 'neutral';
  };

  const selectedVersionChapterData = React.useMemo(() => {
    if (!selectedChapter || !hasSelectedVersion) return null as any;
    if (normalizedProofreadVersion === 'Original') {
      return selectedChapter;
    }
    const { sourceLang: srcLang, targetLang: tgtLang } = splitLanguagePair(normalizedProofreadVersion);
    if (!srcLang || !tgtLang) return null as any;
    const translatedChaptersKey = `translated_chapters_${srcLang}_${tgtLang}`;
    const translatedChapters = (book as any)?.[translatedChaptersKey] || [];
    return translatedChapters.find((chap: any) => chap.chapter_guid === selectedChapter.chapter_guid) || null;
  }, [selectedChapter, hasSelectedVersion, normalizedProofreadVersion, book]);

  const hasPendingProofreadReview = React.useMemo(() => {
    return hasPendingProofreadReviewForRow(selectedVersionChapterData);
  }, [selectedVersionChapterData]);

  React.useEffect(() => {
    if (!isVisible || selectedChapter) return;
    if (isProofreadChecked && bookProofreadActionUnavailable) {
      setIsProofreadChecked(false);
    }
    if (isEditingNotesChecked && bookEditingNotesActionUnavailable) {
      setIsEditingNotesChecked(false);
    }
  }, [
    bookEditingNotesActionUnavailable,
    bookProofreadActionUnavailable,
    isEditingNotesChecked,
    isProofreadChecked,
    isVisible,
    selectedChapter,
    setIsEditingNotesChecked,
    setIsProofreadChecked,
  ]);

  const proofreadActionUnavailable = Boolean(isProofreadChecked && bookProofreadActionUnavailable);
  const editingNotesActionUnavailable = Boolean(isEditingNotesChecked && bookEditingNotesActionUnavailable);
  const hasActionableProofreadSelection = Boolean(effectiveIsProofreadChecked && (selectedChapter || !bookProofreadActionUnavailable));
  const hasActionableEditingNotesSelection = Boolean(effectiveIsEditingNotesChecked && (selectedChapter || !bookEditingNotesActionUnavailable));
  const hasActionableSelectedAction = Boolean(hasActionableProofreadSelection || hasActionableEditingNotesSelection);

  // Group status messages to display
  const statusMessages = React.useMemo(() => {
    const messages: Array<{
      type: 'error' | 'warning' | 'success';
      message: string;
    }> = [];

    if (!hasSelectedVersion) {
      messages.push({
        type: 'error',
        message: 'Please select a version to proofread to proceed.',
      });
      return messages;
    }

    // Warning messages (orange)
    if (selectedChapter && effectiveIsEditingNotesChecked && hasChapterBeenEditNotes) {
      messages.push({
        type: 'warning',
        message: 'This chapter already has editing notes.',
      });
    }

    // Error messages (red)
    if (needsModel) {
      messages.push({
        type: 'error',
        message: 'Please select an AI model to continue.',
      });
    }

    if (proofreadActionUnavailable) {
      messages.push({
        type: hasActionableEditingNotesSelection ? 'warning' : 'error',
        message: proofreadingPassSummary?.blockingReason
          || (pendingProofreadReviewCount > 0
            ? 'No chapters are eligible for proofreading. Chapters with pending proofread review must be saved or discarded before they can be re-proofread.'
            : 'No chapters are eligible for proofreading.'),
      });
    }

    if (!selectedChapter && effectiveIsProofreadChecked && isLoadingProofreadingPassSummary) {
      messages.push({
        type: 'warning',
        message: 'Checking proofreading pass eligibility.',
      });
    } else if (selectedBulkProofreadModeBlocked && !proofreadActionUnavailable) {
      messages.push({
        type: 'error',
        message: proofreadingPassSummary?.blockingReason
          || 'The selected proofreading pass is not currently available for this version.',
      });
    }

    if (editingNotesActionUnavailable) {
      messages.push({
        type: hasActionableProofreadSelection ? 'warning' : 'error',
        message: 'All chapters have editing notes.',
      });
    }

    if (isLoadingTranslationAvailability && proofreadVersion && proofreadVersion !== 'Original') {
      messages.push({
        type: 'warning',
        message: selectedChapter
          ? 'Checking translated chapter availability for this language.'
          : 'Checking translated chapters for this language.',
      });
    }

    if (!selectedChapter && normalizedProofreadVersion && hasUntranslatedChaptersForProofread) {
      messages.push({
        type: 'error',
        message: 'Unable to proofread this language. Not all chapters have been translated to the target language.',
      });
    }

    if (selectedChapter && normalizedProofreadVersion && !canChapterBeProofread) {
      messages.push({
        type: 'error',
        message: 'Unable to proofread. This chapter has not been translated to the specified language.',
      });
    }

    if (selectedChapter && effectiveIsProofreadChecked && hasPendingProofreadReview) {
      messages.push({
        type: 'warning',
        message: 'This chapter has an unsaved proofread review. Starting a new proofread will replace it.',
      });
    }

    if (needsFrenchTone) {
      messages.push({
        type: 'error',
        message: 'Please choose a French narrative style before proofreading.',
      });
    }

    // Success messages (green)
    if (!selectedChapter && effectiveIsProofreadChecked && hasEligibleProofreadChapters
      && !selectedBulkProofreadModeBlocked && !bulkProofreadEligibilityPending) {
      const selectedCount = selectedBulkProofreadModeOption?.count || 0;
      const passLabel = selectedBulkProofreadModeOption?.label || 'Proofread missing chapters';
      messages.push({
        type: bulkProofreadMode === 'next_pass' ? 'warning' : 'success',
        message:
          bulkProofreadMode === 'next_pass'
            ? `${passLabel} will create a new charge for ${selectedCount} content chapter${selectedCount === 1 ? '' : 's'} and replace saved proofread output after review. This is not needed to finish the current pass.`
            : bulkProofreadMode === 'retry_failed'
              ? `${passLabel} will submit only failed or incomplete chapters from the current pass.`
              : 'ScribeShadow will proofread only chapters without saved proofread output. Chapters with pending proofread review are skipped until those reviews are saved or discarded.',
      });
    }

    if (!selectedChapter && effectiveIsProofreadChecked && (proofreadingPassSummary?.structuralChapterCount || 0) > 0) {
      const count = proofreadingPassSummary?.structuralChapterCount || 0;
      messages.push({
        type: 'success',
        message: `${count} structural EPUB section${count === 1 ? '' : 's'} will be preserved but not submitted or charged.`,
      });
    }

    if (!selectedChapter && effectiveIsProofreadChecked && pendingProofreadReviewCount > 0) {
      messages.push({
        type: 'warning',
        message: `${pendingProofreadReviewCount} chapter${pendingProofreadReviewCount === 1 ? '' : 's'} with pending proofread review will be skipped until saved or discarded.`,
      });
    }

    if (!selectedChapter && effectiveIsEditingNotesChecked && hasUnproofreadNotesChapters) {
      messages.push({
        type: 'success',
        message: 'ScribeShadow will only provide editing notes for chapters that do not yet have editing notes.',
      });
    }

    return messages;
  }, [
    selectedChapter,
    effectiveIsProofreadChecked,
    effectiveIsEditingNotesChecked,
    hasChapterBeenEditNotes,
    hasPendingProofreadReview,
    needsModel,
    proofreadActionUnavailable,
    selectedBulkProofreadModeBlocked,
    bulkProofreadEligibilityPending,
    editingNotesActionUnavailable,
    hasActionableEditingNotesSelection,
    hasActionableProofreadSelection,
    hasEligibleProofreadChapters,
    hasUnproofreadNotesChapters,
    pendingProofreadReviewCount,
    proofreadingPassSummary?.blockingReason,
    bulkProofreadMode,
    selectedBulkProofreadModeOption,
    isLoadingProofreadingPassSummary,
    isLoadingTranslationAvailability,
    hasUntranslatedChaptersForProofread,
    hasSelectedVersion,
    normalizedProofreadVersion,
    canChapterBeProofread,
    needsFrenchTone,
  ]);
  const blockingStatusMessages = statusMessages.filter(msg => msg.type === 'error');
  const contextualStatusMessages = statusMessages.filter(msg => msg.type !== 'error');
  const readinessVisibleLimit = 3;
  const readinessPrimaryMessages = [
    ...blockingStatusMessages,
    ...contextualStatusMessages,
  ];
  const readinessVisibleMessages = showReadinessDetails
    ? readinessPrimaryMessages
    : readinessPrimaryMessages.slice(0, readinessVisibleLimit);
  const readinessOverflowCount = Math.max(readinessPrimaryMessages.length - readinessVisibleLimit, 0);

  const normalizedBookFrenchTone = React.useMemo(() => {
    const raw = ((book?.frenchNarrativeTone as FrenchNarrativeTone | undefined) || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.startsWith('modern')) return 'modern';
    if (raw.startsWith('classical')) return 'classical';
    if (raw.startsWith('present')) return 'present';
    return raw;
  }, [book?.frenchNarrativeTone]);
  const recommendedFrenchToneValue = normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE;
  const frenchToneSummary = React.useMemo<React.ReactNode>(() => {
    if (!isFrenchTarget) return '';
    if (!frenchTone) {
      return 'French proofreading needs a narrative voice, so pick the option that fits this book (this setting disappears for other languages).';
    }
    const isUsingSavedTone =
      normalizedBookFrenchTone && frenchTone === normalizedBookFrenchTone;
    const intro = 'French proofreading needs a narrative voice.';
    const savedSuffix = isUsingSavedTone
      ? ' We already detected and saved this voice for your book, so it will stay consistent.'
      : '';
    const summaryMap: Record<string, React.ReactNode> = {
      [AUTO_FRENCH_TONE_VALUE]: (
        <>
          {intro} We'll let <strong>AI Detect</strong> whether this book needs a
          <strong> Modern/Accessible</strong> or <strong>Classical/Literary</strong> voice and match the tense per chapter.
        </>
      ),
      modern: (
        <>
          {intro} We'll proofread the whole book using a <strong>Modern/Accessible Past Tense</strong> voice while matching tense per chapter.
        </>
      ),
      classical: (
        <>
          {intro} We'll proofread the whole book using a <strong>Classical/Literary Past Tense</strong> voice while matching tense per chapter.
        </>
      ),
      present: (
        <>
          {intro} We'll proofread the entire book using a consistent <strong>Present Tense</strong> narration.
        </>
      ),
    };
    const summaryNode = summaryMap[frenchTone] || frenchTone;
    return (
      <>
        {summaryNode}
        {savedSuffix}
      </>
    );
  }, [isFrenchTarget, frenchTone, normalizedBookFrenchTone]);

  // Ensure default tone for French
  React.useEffect(() => {
    if (!isVisible) return;
    if (!isFrenchTarget) return;
    if (!frenchTone) {
      setFrenchTone(normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE);
    }
  }, [isVisible, isFrenchTarget, frenchTone, setFrenchTone, normalizedBookFrenchTone]);

  const handleFrenchToneChange = (value?: string) => {
    setFrenchTone(value || '')
  }

  const handleProofreadVersionChange = (value: string) => {
    setProofreadVersion(value);
    setSelectedAIModel(''); // Reset AI model when language changes
    
    // Set default quotation style for the language
    const normalizedValue = normalizeProofreadVersionValue(value);
    const language = normalizedValue === 'Original'
      ? bookLanguage
      : getTargetLanguageFromPair(normalizedValue) || '';
    
    const defaultStyle = getDefaultQuotationStyle(language);
    setQuotationStyle(defaultStyle);
    
    if (normalizedValue !== "Original") {
      setIsEditingNotesChecked(false);
    }
  };

  React.useEffect(() => {
    if (!isVisible) return
    debugStamp(`ProofreadModal:language=${currentLanguage || 'none'} model=${selectedAIModel || 'none'}`)
  }, [isVisible, currentLanguage, selectedAIModel, debugStamp])

  const disableProofreadButton =
    !normalizedProofreadVersion ||
    !hasEnoughCreditsForProofread ||
    !hasEffectiveProofreadAction ||
    !hasActionableSelectedAction ||
    adjustedCost === 0 ||
    (selectedChapter && !canChapterBeProofread) ||
    (!selectedChapter && hasUntranslatedChaptersForProofread) ||
    isLoadingTranslationAvailability ||
    bulkProofreadEligibilityPending ||
    selectedBulkProofreadModeBlocked ||
    needsModel ||
    needsQuotationStyle ||
    needsFrenchTone;

  const footerBlockerMessage = React.useMemo(() => {
    if (!disableProofreadButton) return '';
    if (!normalizedProofreadVersion) return 'Proofreading target required.';
    if (!hasEffectiveProofreadAction) {
      if (!isProofreadChecked && !isEditingNotesChecked) return 'Select at least one proofreading action.';
      return 'Select at least one available proofreading action.';
    }
    if (needsModel) return 'AI model required.';
    if (needsQuotationStyle) return 'Quotation style required.';
    if (needsFrenchTone) return 'French narrative style required.';
    if (isLoadingTranslationAvailability) return selectedChapter
      ? 'Checking chapter availability.'
      : 'Checking translated chapters.';
    if (bulkProofreadEligibilityPending) {
      return 'Checking proofreading pass eligibility.';
    }
    if (selectedBulkProofreadModeBlocked) {
      return proofreadingPassSummary?.blockingReason
        || 'The selected proofreading pass is not currently available for this version.';
    }
    if (selectedChapter && !canChapterBeProofread) return 'Chapter translation required.';
    if (!selectedChapter && hasUntranslatedChaptersForProofread) return 'All chapters must be translated first.';
    if (!hasActionableSelectedAction) {
      if (proofreadActionUnavailable && editingNotesActionUnavailable) return 'No selected actions have eligible chapters.';
      if (proofreadActionUnavailable) {
        return pendingProofreadReviewCount > 0
          ? 'Save or discard pending proofread reviews before re-proofreading.'
          : 'All chapters have been proofread.';
      }
      if (editingNotesActionUnavailable) return 'No chapters need editing notes.';
    }
    if (proofreadActionUnavailable) {
      return pendingProofreadReviewCount > 0
        ? 'Save or discard pending proofread reviews before re-proofreading.'
        : 'All chapters have been proofread.';
    }
    if (editingNotesActionUnavailable) return 'No chapters need editing notes.';
    if (!hasEnoughCreditsForProofread && adjustedCost > 0) return 'Credits required exceed balance.';
    if (typeof availableCredits !== 'number') return 'Credit balance is still loading.';
    if (adjustedCost === 0) return 'No credit-cost proofreading action is currently available.';
    return blockingStatusMessages[0]?.message || '';
  }, [
    adjustedCost,
    availableCredits,
    blockingStatusMessages,
    canChapterBeProofread,
    disableProofreadButton,
    editingNotesActionUnavailable,
    hasEligibleProofreadChapters,
    hasEnoughCreditsForProofread,
    hasActionableSelectedAction,
    hasEffectiveProofreadAction,
    hasSelectedVersion,
    hasUnproofreadNotesChapters,
    hasUntranslatedChaptersForProofread,
    effectiveIsProofreadChecked,
    isEditingNotesChecked,
    isLoadingProofreadingPassSummary,
    isLoadingTranslationAvailability,
    isProofreadChecked,
    needsFrenchTone,
    needsModel,
    needsQuotationStyle,
    normalizedProofreadVersion,
    pendingProofreadReviewCount,
    proofreadActionUnavailable,
    selectedBulkProofreadModeBlocked,
    bulkProofreadEligibilityPending,
    proofreadingPassSummary?.blockingReason,
    selectedChapter,
  ]);

  const modelAdjustmentNode = (() => {
    const modelDiscountPct = getModelDiscountPercent();
    const modelPremiumPct = getModelPremiumPercent();
    const batchPct = effectiveUseBatch ? 5 : 0;
    if (!modelDiscountPct && !modelPremiumPct && !batchPct) return null;
    return (
      <>
        {modelDiscountPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--success modal-token-tag--compact">
            {modelDiscountPct}% OFF
          </Tag>
        )}
        {modelPremiumPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--warning modal-token-tag--compact">
            +{modelPremiumPct}% Premium
          </Tag>
        )}
        {batchPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--success modal-token-tag--compact">
            Queued {batchPct}% OFF
          </Tag>
        )}
      </>
    );
  })();

  const proofreadCostRows = React.useMemo<ModalCostBreakdownRow[]>(() => {
    const rows: ModalCostBreakdownRow[] = []
    if (hasEffectiveProofreadAction) {
      const actionLabels: string[] = []
      if (effectiveIsProofreadChecked) {
        actionLabels.push(
          !selectedChapter && selectedBulkProofreadModeOption?.label
            ? selectedBulkProofreadModeOption.label
            : 'Proofread text',
        )
      }
      if (effectiveIsEditingNotesChecked) {
        actionLabels.push('Generate editing notes')
      }
      rows.push({
        key: 'proofread-selected-actions',
        label: actionLabels.join(' + ') || 'Selected proofreading action',
        amountCredits: adjustedCost,
        displayValue: `${formatCredits(adjustedCost)} credits`,
        explanation: selectedChapter
          ? 'Runs on the selected chapter.'
          : 'Runs on eligible chapters in the selected version.',
      })
    }
    const modelDiscountPct = getModelDiscountPercent()
    const modelPremiumPct = getModelPremiumPercent()
    const batchPct = effectiveUseBatch ? 5 : 0
    if (modelDiscountPct > 0) {
      rows.push({
        key: 'proofread-model-discount',
        label: 'Model discount',
        displayValue: `-${modelDiscountPct}%`,
        tone: 'success',
      })
    }
    if (modelPremiumPct > 0) {
      rows.push({
        key: 'proofread-model-premium',
        label: 'Model premium',
        displayValue: `+${modelPremiumPct}%`,
        tone: 'warning',
      })
    }
    if (batchPct > 0) {
      rows.push({
        key: 'proofread-queued-discount',
        label: 'Queued proofreading discount',
        displayValue: `-${batchPct}%`,
        tone: 'success',
      })
    }
    return rows
  }, [
    adjustedCost,
    effectiveIsEditingNotesChecked,
    effectiveIsProofreadChecked,
    effectiveUseBatch,
    formatCredits,
    getModelDiscountPercent,
    getModelPremiumPercent,
    hasEffectiveProofreadAction,
    selectedBulkProofreadModeOption?.label,
    selectedChapter,
  ])

  const footerCostSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite">
      {hasEffectiveProofreadAction ? (
        <span>
          <strong>{formatCredits(adjustedCost)} credits</strong> required
        </span>
      ) : (
        <span>Select a proofreading action to calculate cost</span>
      )}
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>
          {typeof availableCredits === 'number' ? formatCredits(availableCredits) : 'Loading...'}
        </strong>{' '}
        available
      </span>
      {hasEffectiveProofreadAction && !hasEnoughCreditsForProofread && adjustedCost > 0 ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Top-up needed</span>
        </>
      ) : null}
      {hasEffectiveProofreadAction && modelAdjustmentNode ? (
        <span className="proofread-modal-footer-tags">{modelAdjustmentNode}</span>
      ) : null}
      {disableProofreadButton && footerBlockerMessage ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="proofread-modal-footer-blocker">Action needed: {footerBlockerMessage}</span>
        </>
      ) : null}
      <Button
        type="link"
        size="small"
        className="translation-modal-footer-summary__details"
        aria-label="View cost breakdown"
        onClick={() => setIsCostBreakdownModalVisible(true)}
        data-tour-id="proofread-modal-cost"
      >
        View details
      </Button>
    </div>
  );
  const proofreadTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'proofreadModalTourSeen',
    enabled: isVisible,
    autoStart: true,
  });
  const proofreadTutorialUrl = !proofreadVersion || proofreadVersion === 'Original'
    ? tutorialVideoLinks.originalProofread
    : tutorialVideoLinks.translatedProofread;
  const proofreadTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Choose the proofreading target',
      description: renderAppTourPanel(
        <>
          Select the original manuscript or a translated version, then decide whether this run applies to the book or current chapter.
          <AppTourVideoLink href={proofreadTutorialUrl}>
            Watch the proofreading tutorial
          </AppTourVideoLink>
        </>
      ),
      target: tourTarget('proofread-modal-scope'),
    },
    {
      title: 'Pick actions',
      description: renderAppTourPanel('Choose text proofreading, editing notes, or both. Some options only apply to the original manuscript.'),
      target: tourTarget('proofread-modal-actions', 'proofread-modal-scope'),
    },
    {
      title: 'Review quality settings',
      description: renderAppTourPanel('Set the model, quotation handling, language-specific tone, and any additional instructions before running.'),
      target: tourTarget('proofread-modal-settings', 'proofread-modal-actions'),
    },
    {
      title: 'Check cost and blockers',
      description: renderAppTourPanel('Readiness shows blockers, while the cost breakdown shows required credits and remaining balance.'),
      target: tourTarget('proofread-modal-cost', 'proofread-modal-settings'),
    },
  ]), [proofreadTutorialUrl]);

  return (
    <>
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">{selectedChapter ? "Proofread Chapter" : "Proofread Book"}</span>
          <Button
            type="text"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={proofreadTour.start}
            data-tour-id="proofread-modal-tour-trigger"
            className="translation-modal-titlebar__tour app-tour-trigger"
          >
            Take a tour
          </Button>
        </div>
      }
      visible={isVisible}
      onCancel={onCancel}
      centered
      className="translation-modal-footer-stable proofread-modal-shell"
      footer={[
        <span key="cost-summary" data-testid="tutorial:proofread:footer-summary">
          {footerCostSummary}
        </span>,
        <Button key="cancel" onClick={onCancel} data-testid="tutorial:proofread:cancel">
          Cancel
        </Button>,
        <Button 
          key="proofread" 
          type="primary" 
          className="app-primary-cta"
          onClick={onProofread} 
          disabled={disableProofreadButton}
          data-tour-id="proofread-modal-submit"
          data-testid="tutorial:proofread:start"
        >
          {hasPendingProofreadReview && effectiveIsProofreadChecked ? 'Review Replacement' : 'Start Proofreading'}
        </Button>
      ]}
      width="min(980px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(84vh, calc(100vh - 160px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '10px 14px 8px',
      }}
    >
      <div className="translation-modal-body proofread-modal-body" data-testid="tutorial:proofread:modal">
        <section className="translation-modal-block translation-modal-block--scope proofread-modal-section proofread-modal-section--scope" aria-labelledby="proofread-target-heading" data-tour-id="proofread-modal-scope" data-testid="tutorial:proofread:scope">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="proofread-target-heading">Proofreading scope</h3>
              <p>Choose the version to proofread before selecting actions and settings.</p>
            </div>
          </div>
          <div className="proofread-modal-target-grid">
            <div className="translation-scope-field proofread-modal-target-field">
              <span className="translation-modal-kicker">Proofreading target</span>
              <Select
                data-testid="tutorial:proofread:target-version"
                value={proofreadVersion}
                onChange={handleProofreadVersionChange}
                placeholder="Select a version to proofread"
                style={{ width: '100%' }}
                suffixIcon={<span className="ant-select-suffix"><DownOutlined className="modal-token-icon"/></span>}
              >
                <Select.Option value="">Select version</Select.Option>
                <Select.Option value="Original">{originalLanguageLabel}</Select.Option>
                {book.translation_pairs && book.translation_pairs.map((pair: string) => (
                  <Select.Option key={pair} value={pair}>{formatLangDisplayText(pair)}</Select.Option>
                ))}
              </Select>
              <div className="translation-scope-field-helper">
                {selectedChapter ? 'Runs on the selected chapter.' : 'Book runs use eligible chapters in this version.'}
              </div>
            </div>
          </div>
        </section>

        <section className="translation-modal-block proofread-modal-section proofread-modal-section--actions" aria-labelledby="proofread-actions-heading" data-tour-id="proofread-modal-actions" data-testid="tutorial:proofread:actions">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="proofread-actions-heading">Actions</h3>
              <p>Choose proofreading, editing notes, or both.</p>
            </div>
          </div>
          <div className="proofread-modal-action-list">
            <div
              className={`translation-quality-control proofread-modal-action-option${effectiveIsProofreadChecked ? ' proofread-modal-action-option--selected' : ''}${proofreadSelectionDisabled ? ' proofread-modal-action-option--disabled' : ''}`}
              data-testid="tutorial:proofread:proofread-text-action"
            >
              <Checkbox
                checked={effectiveIsProofreadChecked}
                onChange={e => setIsProofreadChecked(e.target.checked)}
                disabled={proofreadSelectionDisabled}
                data-testid="tutorial:proofread:proofread-text-checkbox"
              >
                <span style={{ fontWeight: 600 }}>Proofread text</span>
              </Checkbox>
              <Tooltip title="Proofread the chapter or book, improving language and readability.">
                <QuestionCircleOutlined className="modal-token-icon proofread-modal-inline-icon" />
              </Tooltip>
              <div className="proofread-modal-action-helper">
                {isCheckingBookProofreadEligibility
                  ? 'Checking saved proofreads, failed chapters, and pending reviews for this version.'
                  : bookProofreadActionUnavailable
                  ? 'All chapters already have saved proofreads or pending reviews.'
                  : selectedChapter
                    ? 'Runs on this chapter only.'
                    : 'Runs across eligible chapters in the selected version.'}
              </div>

              {repeatPassAvailable && bulkProofreadMode !== 'next_pass' && (
                <Button onClick={() => {
                  setBulkProofreadMode?.('next_pass');
                  setIsProofreadChecked(true);
                }}>
                  Run another proofreading pass
                </Button>
              )}
              {effectiveIsProofreadChecked && (
                <div className="proofread-modal-action-nested">
                  {!selectedChapter && (
                    <div className="proofread-modal-run-control">
                      <div className="proofread-modal-run-control-header">
                        <span className="translation-modal-kicker proofread-modal-run-control-title">
                          Proofread pass
                        </span>
                        {isLoadingProofreadingPassSummary ? (
                          <span className="proofread-modal-run-control-status">
                            <LoadingOutlined className="proofread-modal-run-control-status-icon" spin />
                            Checking current status
                          </span>
                        ) : null}
                      </div>
                      <Select
                        data-testid="tutorial:proofread:proofread-pass"
                        value={selectedBulkProofreadModeOption ? bulkProofreadMode : undefined}
                        onChange={(value) => setBulkProofreadMode?.(value as ProofreadPassMode)}
                        disabled={isLoadingProofreadingPassSummary || bulkProofreadModeOptions.length === 0}
                        loading={isLoadingProofreadingPassSummary}
                        placeholder={isLoadingProofreadingPassSummary ? 'Checking proofread status...' : 'Select proofread pass'}
                        style={{ width: '100%' }}
                        suffixIcon={<span className="ant-select-suffix"><DownOutlined className="modal-token-icon"/></span>}
                      >
                        {bulkProofreadModeOptions.map(option => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label} ({option.count} chapter{option.count === 1 ? '' : 's'})
                          </Select.Option>
                        ))}
                      </Select>
                      <div className="proofread-modal-action-helper proofread-modal-run-helper">
                        {isLoadingProofreadingPassSummary
                          ? 'ScribeShadow is checking which chapters can run without recharging completed work.'
                          : selectedBulkProofreadModeOption
                            ? `${selectedBulkProofreadModeOption.count} chapter${selectedBulkProofreadModeOption.count === 1 ? '' : 's'} selected for this pass.`
                            : proofreadingPassSummary?.blockingReason
                              || 'No proofread pass is currently available for this version.'}
                      </div>
                    </div>
                  )}
                  <div className="proofread-modal-toggle-row" data-testid="tutorial:proofread:auto-accept">
                    <Checkbox
                      checked={autoAcceptAllChanges}
                      onChange={e => setAutoAcceptAllChanges(e.target.checked)}
                    >
                      <span style={{ fontWeight: 500 }}>
                        Automatically accept all proofread changes
                      </span>
                    </Checkbox>
                    <div className="proofread-modal-action-helper">
                      Skips manual review for this new proofread run.
                    </div>
                  </div>
                  {isSavingAutoAcceptPreference ? (
                    <div className="proofread-modal-action-helper proofread-modal-action-helper--accent">
                      Saving preference...
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div
              className={`translation-quality-control proofread-modal-action-option${effectiveIsEditingNotesChecked ? ' proofread-modal-action-option--selected' : ''}${editingNotesSelectionDisabled ? ' proofread-modal-action-option--disabled' : ''}`}
              data-testid="tutorial:proofread:editing-notes-action"
            >
              <Checkbox
                checked={effectiveIsEditingNotesChecked}
                onChange={e => setIsEditingNotesChecked(e.target.checked)}
                disabled={editingNotesSelectionDisabled}
                data-testid="tutorial:proofread:editing-notes-checkbox"
              >
                <span style={{ fontWeight: 600 }}>Generate editing notes</span>
              </Checkbox>
              {normalizedProofreadVersion !== "Original" ? (
                <Tooltip title="Editing notes are only available when proofreading the original version.">
                  <QuestionCircleOutlined className="modal-token-icon proofread-modal-inline-icon" />
                </Tooltip>
              ) : (
                <Tooltip title="Generate editing notes with suggestions for further improvements.">
                  <QuestionCircleOutlined className="modal-token-icon proofread-modal-inline-icon" />
                </Tooltip>
              )}
              <div className="proofread-modal-action-helper">
                {normalizedProofreadVersion !== "Original"
                  ? 'Available only for the original version.'
                  : bookEditingNotesActionUnavailable
                    ? 'All chapters already have editing notes.'
                    : selectedChapter
                      ? 'Creates chapter-level editing notes.'
                      : 'Creates editing notes for eligible chapters without notes.'}
              </div>
            </div>
          </div>
        </section>

        <section className="translation-modal-block proofread-modal-section proofread-modal-section--settings" aria-labelledby="proofread-quality-heading" data-tour-id="proofread-modal-settings" data-testid="tutorial:proofread:settings">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="proofread-quality-heading">Quality and behavior</h3>
              <p>Set model and language behavior.</p>
            </div>
          </div>
          <div className="translation-quality-grid">
            <div className="translation-quality-control">
              <span className="translation-modal-kicker translation-quality-label">
                AI Model
                {needsModel && (
                  <Tag className="modal-token-tag modal-token-tag--danger">
                    Required
                  </Tag>
                )}
              </span>
              <AIModelSelect
                workflow="proofreading"
                value={selectedAIModel}
                onChange={setSelectedAIModel}
                language={currentLanguage}
                sourceLanguage={sourceForCurrentLanguage}
                isLangTesterUser={isLangTesterUser}
                autoSelect={isVisible}
              />
            </div>

            <div className="translation-quality-control">
              <span className="translation-modal-kicker translation-quality-label">
                Quotation style
                <Tooltip title={getQuotationTooltip(currentLanguage)}>
                  <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive" />
                </Tooltip>
                {needsQuotationStyle && (
                  <Tag className="modal-token-tag modal-token-tag--danger">
                    Required
                  </Tag>
                )}
              </span>
              {(() => {
                const quotationConfig = getQuotationStyle(currentLanguage);

                if (!quotationConfig) {
                  return (
                    <>
                      <Select disabled value="unknown" style={{ width: '100%' }}>
                        <Select.Option value="unknown"> </Select.Option>
                      </Select>
                      <div className="proofread-modal-muted-helper"></div>
                    </>
                  );
                }

                if (quotationConfig.options) {
                  return (
                    <Select
                      value={quotationStyle}
                      onChange={setQuotationStyle}
                      placeholder="Please select a quotation style"
                      style={{
                        width: '100%',
                      }}
                      status={needsQuotationStyle ? 'error' : undefined}
                      suffixIcon={
                        <span className="ant-select-suffix">
                          <DownOutlined className="modal-token-icon"/>
                        </span>
                      }
                    >
                      {quotationConfig.options.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label} ({option.marks})
                          {option.isDefault && (
                            <RecommendedTag style={{ position: 'relative', top: '-0.5px' }} />
                          )}
                        </Select.Option>
                      ))}
                    </Select>
                  );
                }

                if (quotationConfig.fixed) {
                  return (
                    <>
                      <Select disabled value="fixed" style={{ width: '100%' }}>
                        <Select.Option value="fixed">
                          {quotationConfig.fixed.name} ({quotationConfig.fixed.marks})
                        </Select.Option>
                      </Select>
                    </>
                  );
                }

                return null;
              })()}
            </div>

            {currentLanguage === 'French' && (
              <div className="translation-quality-control proofread-modal-wide-control">
                <span className="translation-modal-kicker translation-quality-label">
                  Narrative style
                  <Tooltip
                    styles={{
                      root: { maxWidth: 520 },
                      body: { maxWidth: 520 },
                    }}
                    title={<FrenchNarrationTooltipContent />}
                  >
                    <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive" />
                  </Tooltip>
                  <FrenchNarrationInfo linkStyle={{ marginLeft: 8 }} variant="simple" />
                  {!frenchTone && (
                    <Tag className="modal-token-tag modal-token-tag--danger">
                      Required
                    </Tag>
                  )}
                </span>
                <Select
                  value={frenchTone || undefined}
                  onChange={handleFrenchToneChange}
                  placeholder="Select a narrative voice"
                  style={{ width: '100%' }}
                  status={needsFrenchTone ? 'error' : undefined}
                  allowClear
                  suffixIcon={<span className="ant-select-suffix"><DownOutlined className="modal-token-icon"/></span>}
                >
                  <Select.Option value={AUTO_FRENCH_TONE_VALUE}>
                    AI Detect (recommend voice & tense)
                    {recommendedFrenchToneValue === AUTO_FRENCH_TONE_VALUE && <RecommendedTag />}
                  </Select.Option>
                  <Select.Option value="modern">
                    Modern/Accessible voice
                    {recommendedFrenchToneValue === 'modern' && <RecommendedTag />}
                  </Select.Option>
                  <Select.Option value="classical">
                    Classical/Literary voice
                    {recommendedFrenchToneValue === 'classical' && <RecommendedTag />}
                  </Select.Option>
                  <Select.Option value="present">
                    Present Tense (source narration mainly present)
                    {recommendedFrenchToneValue === 'present' && <RecommendedTag />}
                  </Select.Option>
                </Select>
                {frenchToneSummary && (
                  <div className="proofread-modal-accent-helper">
                    {frenchToneSummary}
                  </div>
                )}
                {needsFrenchTone && (
                  <div className="proofread-modal-error-helper">
                    Narrative voice is required for French proofreading.
                  </div>
                )}
              </div>
	            )}
	          </div>
            <div className="translation-quality-control proofread-modal-wide-control proofread-modal-explanation-language-control">
              <span className="translation-modal-kicker translation-quality-label">
                Explanation language
                <Tooltip title="Correction reasons will be written in this language, even when the manuscript is in another language.">
                  <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive" />
                </Tooltip>
              </span>
              <Select
                value={explanationLanguage || 'English'}
                onChange={setExplanationLanguage}
                placeholder="Select explanation language"
                style={{ width: '100%' }}
                disabled={isSavingExplanationLanguagePreference}
                optionFilterProp="children"
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                suffixIcon={<span className="ant-select-suffix"><DownOutlined className="modal-token-icon"/></span>}
              >
                {LANGUAGE_CONFIG.baseLanguages.map((language: string) => (
                  <Select.Option key={language} value={language}>
                    {language}
                  </Select.Option>
                ))}
              </Select>
            </div>
	          <div className="translation-readiness-panel proofread-modal-instructions-panel">
	            <div className="proofread-modal-instructions-header">
	              <div className="translation-modal-subheading">
	                Additional instructions (optional)
	                <Tooltip title="You can provide specific instructions or preferences for the proofreading process here.">
	                  <QuestionCircleOutlined className="modal-token-icon proofread-modal-inline-icon" />
	                </Tooltip>
	              </div>
	            </div>
	            {effectiveIsProofreadChecked ? (
	              <Input.TextArea
	                value={proofreadAdditionalInstructions}
	                onChange={(e: any) => {
	                  const nextValue = typeof e === 'string' ? e : e.target.value;
	                  setProofreadAdditionalInstructions(nextValue);
	                }}
	                placeholder="Enter any additional instructions for proofreading"
	                autoSize={{ minRows: 1, maxRows: 2 }}
	                style={{ width: '100%' }}
	              />
	            ) : (
	              <div className="proofread-modal-instructions-locked">
	                Select Proofread text to add proofreader instructions. Editing-note-only runs do not use these notes.
	              </div>
	            )}
	          </div>
	        </section>

        <section className="translation-modal-block proofread-modal-section proofread-modal-section--readiness" aria-labelledby="proofread-readiness-heading" data-testid="tutorial:proofread:cost-readiness">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="proofread-readiness-heading">Readiness</h3>
              <p>Confirm blockers and optional guidance before proofreading.</p>
            </div>
          </div>
          <div className="translation-readiness-panels">
            <div className="translation-readiness-panel proofread-modal-credit-panel">
              <div className={disableProofreadButton ? 'proofread-modal-readiness-summary is-needed' : 'proofread-modal-readiness-summary is-ready'}>
                {disableProofreadButton
                  ? footerBlockerMessage
                    ? `Requirement: ${footerBlockerMessage}`
                    : 'Resolve the highlighted requirements before proofreading.'
                  : 'Ready to start the selected proofreading action.'}
              </div>
            </div>

            {!hasEnoughCreditsForProofread && adjustedCost > 0 && (
              <InsufficientCreditsAlert
                message="You do not have enough credits for this proofreading."
                shortfallCredits={proofreadShortfallCredits}
                onTopUpCredits={onTopUpCredits ? handleTopUpCredits : undefined}
                style={{ marginBottom: 0 }}
              />
            )}

            {batchFeatureEnabled && (
              <div className="translation-readiness-panel">
                <div className="translation-modal-subheading">
                  Queued Proofreading
                  <Tooltip title="Places each chapter in a lower priority queue. Queued proofreading can take up to 24 hours (often faster) and offers a discount.">
                    <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive" />
                  </Tooltip>
                </div>
                <Tooltip
                  title={
                    !isBatchEligible
                      ? 'Queued proofreading is only available for Gemini 2.5 Pro or ChatGPT-5.'
                      : undefined
                  }
                >
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!useBatch}
                      onChange={(e) => setUseBatch(!!e.target.checked)}
                      disabled={!isBatchEligible}
                    />
                    <span className={!isBatchEligible ? 'proofread-modal-disabled-text' : undefined}>
                      Use Queued Proofreading for this job
                    </span>
                  </label>
                </Tooltip>
                <div className="translation-readiness-support-note">
                  Queued proofreading offers a 5% credit discount, but may take up to 24 hours to complete.
                </div>
              </div>
            )}

	            <div className="translation-readiness-panel proofread-modal-readiness-stack">
	              {readinessVisibleMessages.map((msg, index) => (
	                <div
	                  key={`proofread-readiness-${index}`}
	                  className={`translation-readiness-support-note${
	                    msg.type === 'error'
	                      ? ' translation-readiness-support-note--blocking'
	                      : ` translation-readiness-support-note--${getNoticeTone(msg.type)}`
	                  }`}
	                >
	                  {msg.message}
	                </div>
	              ))}
	              {readinessOverflowCount > 0 && (
	                <Button
	                  type="link"
	                  size="small"
	                  className="translation-readiness-details"
	                  onClick={() => setShowReadinessDetails(prev => !prev)}
	                >
	                  {showReadinessDetails
	                    ? 'Hide details'
	                    : `View ${readinessOverflowCount} more readiness detail${readinessOverflowCount === 1 ? '' : 's'}`}
	                </Button>
	              )}
	            </div>

	          </div>
        </section>
      </div>
    </Modal>
    <Modal
      title="Cost breakdown"
      visible={isCostBreakdownModalVisible}
      onCancel={() => setIsCostBreakdownModalVisible(false)}
      width={560}
      footer={[
        <Button key="close-proofread-cost-breakdown" onClick={() => setIsCostBreakdownModalVisible(false)}>
          Close
        </Button>,
      ]}
    >
      <div data-testid="proofread-cost-breakdown-modal">
        <ModalCostBreakdown
          rows={proofreadCostRows}
          totalCredits={hasEffectiveProofreadAction ? adjustedCost : null}
          availableCredits={availableCredits}
          emptyMessage="Select a proofreading action to calculate cost."
          formatCredits={formatCredits}
          footerNote={
            isProofreadChecked
              ? 'If a previous proofread failed, that failed attempt is not charged. This estimate applies to a new successful proofread.'
              : undefined
          }
        />
      </div>
    </Modal>
    <AppTour
      open={proofreadTour.open}
      steps={proofreadTourSteps}
      current={proofreadTour.current}
      onChange={proofreadTour.setCurrent}
      onClose={proofreadTour.close}
      onFinish={proofreadTour.close}
      zIndex={2400}
    />
    </>
  );
};

export default ProofreadModal;
